import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import noop from 'lodash/noop';
import find from 'lodash/find';
import moment from 'moment-timezone';
import * as actions from './HistoricalImport.ducks';

import { createCustomPreset } from '../../helpers/timeFormat';

import Button from 'zignal-landscape/lib/Button';
import Col from 'zignal-landscape/lib/Col';
import DateRangePicker, { DEFAULT_PRESETS } from 'zignal-landscape/lib/DateRangePicker';
import ErrorMessage from 'zignal-landscape/lib/ErrorMessage';
import Field from 'zignal-landscape/lib/Field';
import Grid from 'zignal-landscape/lib/Grid';
import GridContent from 'zignal-landscape/lib/GridContent';
import Heading from 'zignal-landscape/lib/Heading';
import Label from 'zignal-landscape/lib/Label';
import Row from 'zignal-landscape/lib/Row';
import Link from 'zignal-landscape/lib/Link';

import ImportHistory from './components/ImportHistory';
import ImportModal from './components/ImportModal';
import CheckboxSelect from './components/CheckboxSelect';
import ProfileSelect from '../../components/ProfileSelect';

const customPresets = [...DEFAULT_PRESETS, createCustomPreset('last 12 months'), createCustomPreset('last 24 months')]
  .filter( (preset) => preset.label !== 'Last 5 days' );
// Be explicit of the state, not just return everything. This allows for
// clear understanding of what is needed if mixed with other state props.
const mapStateToProps = (
  (state) => {
    return {
      selectedProfiles: state.HistoricalImport.selectedProfiles,
      includeAllProfilesChecked: state.HistoricalImport.includeAllProfilesChecked,
      selectedSources: state.HistoricalImport.selectedSources,
      estimate: state.HistoricalImport.estimate,
      disableImportButton: state.HistoricalImport.disableImportButton,
      history: state.HistoricalImport.history,
      fetchingEstimate: state.HistoricalImport.fetchingEstimate,
      fetchError: state.HistoricalImport.fetchError,
      selectedStartDate: state.HistoricalImport.selectedStartDate,
      selectedEndDate: state.HistoricalImport.selectedEndDate,
      user: state.AppUser.user
    };
  }
);

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  return Object.assign({}, stateProps, dispatchProps, ownProps);
};

export class HistoricalImport extends Component {

  constructor(props) {
    super(props);
    this.state={ timeStartWindow: null };
    const { selectedStartDate: startDate, selectedEndDate: endDate } = props;
    // linked import from profile builder only has one profile selected
    this.props.includeAllProfilesChanged(!this.props.isLinkedImport);
    // initial dates come from backbone, need to set in store
    this.props.dateRangeChanged({startDate, endDate});
  }

  componentDidMount() {
    const {
      selectedProfilesChanged, selectedSourcesChanged, initialProfiles,
      fetchImportHistory, campaignId, initialSources
    } = this.props;
    selectedProfilesChanged(initialProfiles);
    selectedSourcesChanged(initialSources);
    fetchImportHistory(campaignId);
  }

  componentDidUpdate() {
    const { renderNotification, fetchError } = this.props;
    if (fetchError !== null) {
      renderNotification({
        id: 'historic-error',
        type: 'alert-danger',
        message: fetchError
      });
    }
  }

  render() {
    const {
      campaignId,
      campaignName,
      timezoneOffset,

      profiles,
      selectedProfiles,
      selectedProfilesChanged,
      includeAllProfilesChecked,
      includeAllProfilesChanged,

      sources,
      selectedSources,
      selectedSourcesChanged,

      selectedStartDate,
      selectedEndDate,
      dateRangeChanged,

      disableImportButton,
      fetchEstimate,
      fetchingEstimate,
      estimate,
      cancelImport,
      importStories,
      history,
      user
    } = this.props;

    const { timezone } = user;
    const profileOptions = profiles.map((p) => { return { label: p.name, value: p.id }; });
    // show error if selectedEndDate is beyond 2 year
    const dateError = !!(selectedStartDate
                      && moment(selectedStartDate).isBefore(moment().subtract(2, 'year').startOf('day')));
    const dateErrorMessage = dateError ? 'Data import cannot exceed 2 year': '';

    const isFormValid =
      dateError === false &&
      selectedProfiles.length > 0 &&
      selectedSources.length > 0 &&
      selectedStartDate !== null &&
      selectedEndDate !== null;

    const trackBaselineHistoric = {
      campaign: campaignName,
      profiles: selectedProfiles.map((profile)=> {
        return find(profiles, {'id': profile}).name;
      }),
      sources: selectedSources.map((source) => {
        return find(sources, {value: source});
      }),
      dateRange: {startTime: selectedStartDate, endTime: selectedEndDate},
      estimatedStories: estimate
    };

    const startEndDates = this.state.timeStartWindow ? null: {
      startDate: selectedStartDate,
      endDate: selectedEndDate
    };

    return (
      <GridContent>
        <Grid fluid>
          <Row>
            <Col xs={12}>
              <Heading>Historics</Heading>
              <i>
                {'Import up to 2 years of Historical Data from today\'s date for most sources. For data availability by source, see our '}
                <Link
                  href={'https://zignallabs.freshdesk.com/en/support/solutions/articles/24000014386-historics'}
                  target="_blank"
                  rel="noopener noreferrer"
                >Help Article HERE</Link>
              </i>
              <hr />
            </Col>
          </Row>
          <Row>
            <Col xs={5} xsOffset={0}>
              <form>
                <Field>
                  <Label>Datasets</Label>
                  <ProfileSelect
                    options={profileOptions}
                    value={selectedProfiles}
                    onChange={selectedProfilesChanged}
                    includeAll={includeAllProfilesChecked}
                    includeAllProfilesChanged={includeAllProfilesChanged}
                  />
                </Field>
                <Field>
                  <Label>Sources</Label>
                  <CheckboxSelect
                    options={sources}
                    value={selectedSources}
                    onChange={selectedSourcesChanged}
                  />
                </Field>
                <Field>
                  <Label>Date Range</Label>
                  <DateRangePicker
                    startEndDates={startEndDates}
                    startDateError={dateError}
                    endDateError={dateError}
                    isOutsideRange={(day) => {
                      const year = 2;
                      return day.isBefore(moment.tz(timezone).subtract(year, 'year').startOf('day')) ||
                          day.isAfter(moment.tz(timezone).endOf('day'));
                    }}
                    onChange={(e) => {
                      this.setState({timeStartWindow: e.timeStartWindow});
                      dateRangeChanged(e);
                    }
                    }
                    presets={customPresets}
                    inputDisplayFormat={'MM/DD/YYYY h:mma'}
                    timezone={timezone}
                  />
                  {
                    // I had to use this component for the message because startDateErrorMessage
                    // was wrapping to next line
                    dateErrorMessage && <ErrorMessage>{dateErrorMessage}</ErrorMessage>
                  }
                </Field>
                <Field>
                  <Button
                    type="button"
                    buttonStyle="positive"
                    disabled={!isFormValid || fetchingEstimate}
                    onClick={() => {
                      if (isFormValid && fetchingEstimate === false) {
                        fetchEstimate(campaignId, timezoneOffset, trackBaselineHistoric);
                      }
                    }}
                  >
                    {fetchingEstimate ? 'Estimating...' : 'Import Data'}
                  </Button>
                </Field>
              </form>
            </Col>
            <Col xs={6} xsOffset={1}>
              <ImportHistory
                historyItems={history}
                profiles={profiles}
                sources={sources}
                timezoneOffset={timezoneOffset}
              />
            </Col>
          </Row>
        </Grid>
        <ImportModal
          user={user}
          timezoneOffset={timezoneOffset}
          campaignId={campaignId}
          estimate={estimate}
          disableImportButton={disableImportButton}
          cancelImport={cancelImport}
          importStories={importStories}
          trackBaselineHistoric={trackBaselineHistoric}
        />
      </GridContent>
    );
  }
}

HistoricalImport.propTypes = {
  profiles: PropTypes.array,
  selectedProfiles: PropTypes.array,
  selectedProfilesChanged: PropTypes.func,
  includeAllProfilesChecked: PropTypes.bool.isRequired,
  includeAllProfilesChanged: PropTypes.func.isRequired,
  timezoneOffset: PropTypes.number,
  sources: PropTypes.array,
  selectedSources: PropTypes.array,
  selectedSourcesChanged: PropTypes.func,
  selectedStartDate: PropTypes.any,
  selectedEndDate: PropTypes.any,
  campaignId: PropTypes.string,
  campaignName: PropTypes.string,
  fetchError: PropTypes.string,
  fetchingEstimate: PropTypes.bool,
  fetchEstimate: PropTypes.func,
  fetchImportHistory: PropTypes.func,
  history: PropTypes.array,
  estimate: PropTypes.number,
  disableImportButton: PropTypes.bool,
  cancelImport: PropTypes.func,
  importStories: PropTypes.func,
  dateRangeChanged: PropTypes.func,
  renderNotification: PropTypes.func,
  user: PropTypes.object
};

HistoricalImport.defaultProps = {
  profiles: [],
  sources: [],
  selectedProfiles: [],
  selectedSources: [],
  estimate: null,
  disableImportButton: false,
  fetchError: null,
  fetchingEstimate: false,
  user: {},
  renderNotification: noop
};

export default connect(mapStateToProps, actions, mergeProps)(HistoricalImport);
